import type { NavigationProps } from './types'
import type { MenuItem } from '@/cms/fields/types/common'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useState } from 'react'
import { createHeaderClickEvent } from '@/domain/header/analytics'
import { useIntl } from '@/intl'

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { HeaderLink } from './HeaderLink'
import { MenuTrigger } from './Menu'
import styles from './MobileNavigation.module.css'
import { Navbar } from './Navbar'
import { Sheet } from './Sheet'

function MobileBackButton({ onClick }: { onClick: () => void }) {
  const intl = useIntl()
  return (
    <button type="button" className={styles.backButton} onClick={onClick}>
      <Icon glyph="ChevronLeftExtraSmall" className={styles.backButtonIcon} />
      <Typography>{intl.formatMessage({ id: 'back_link' })}</Typography>
    </button>
  )
}

export function MobileNavigation({ data, extraData }: NavigationProps) {
  const intl = useIntl()

  const [sheetIsOpen, setSheetIsOpen] = useState(false)

  // Use controlled state for the mobile menu's back button
  const [openMenu, setOpenMenu] = useState('')
  const [openSubMenu, setOpenSubMenu] = useState('')
  const { sendEvent } = useAnalytics()

  const handleMobileMenuItemClick = (result: string[], children: MenuItem[] | undefined) => {
    sendEvent(
      createHeaderClickEvent({
        result,
        hasChildren: children && children.length > 0,
      }),
    )
  }

  return (
    <Sheet.Root
      onOpenChange={(open) => {
        setSheetIsOpen(open)
      }}
      modal
    >
      <Sheet.Trigger className={styles.sheetTrigger} data-qa="header-mobile-menu" aria-label={sheetIsOpen ? intl.formatMessage({ id: 'menu_close' }) : intl.formatMessage({ id: 'menu_open' })}>{sheetIsOpen ? <Icon glyph="DismissSmall" /> : <Icon glyph="List" />}</Sheet.Trigger>
      <Sheet.Overlay />
      <Sheet.Content data-qa="header-mobile-menu-content" aria-describedby="">
        <Sheet.Title>{intl.formatMessage({ id: 'menu_title' })}</Sheet.Title>
        <Navbar value={openMenu} onValueChange={v => setOpenMenu(v)}>
          <Navbar.List>
            {data && <Typography variant="display-g-regular" as="li" className={styles.heading}>{data.text}</Typography>}
            {data?.children?.map((menuItem, menuItemKey) => (
              <Navbar.Item key={menuItemKey} value={menuItem.text}>
                {menuItem.children && menuItem.children.length > 0
                  ? (
                      <>
                        <MenuTrigger
                          onClick={() => handleMobileMenuItemClick([data.text, menuItem.text], menuItem.children)}
                          onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                        >
                          {menuItem.text}
                          <Icon glyph="ChevronRightExtraSmall" />
                        </MenuTrigger>
                        <Navbar.Content>
                          <MobileBackButton onClick={() => setOpenMenu('')} />
                          <Typography variant="body-l-bold-tight" className={styles.heading}>{menuItem.text}</Typography>
                          <NavigationMenu.Sub value={openSubMenu} onValueChange={v => setOpenSubMenu(v)}>
                            <Navbar.List>
                              {menuItem.children.map((subMenuItem, subMenuItemKey) => (
                                <NavigationMenu.Item key={subMenuItemKey} value={subMenuItem.text}>
                                  {subMenuItem.children && subMenuItem.children.length > 0
                                    ? (
                                        <>
                                          <MenuTrigger
                                            onClick={() =>
                                              handleMobileMenuItemClick(
                                                [data.text, menuItem.text, subMenuItem.text],
                                                subMenuItem.children,
                                              )}
                                            onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                                          >
                                            {subMenuItem.text}
                                            <Icon glyph="ChevronRightExtraSmall" />
                                          </MenuTrigger>
                                          <Navbar.Content>
                                            <MobileBackButton onClick={() => setOpenSubMenu('')} />
                                            <Typography variant="body-l-bold-tight" className={styles.heading}>{subMenuItem.text}</Typography>
                                            <NavigationMenu.Sub>
                                              <Navbar.List>
                                                {subMenuItem.children.map((nestedSubMenuItem, nestedSubMenuItemKey) => (
                                                  <NavigationMenu.Item key={nestedSubMenuItemKey}>
                                                    <HeaderLink
                                                      onClick={() =>
                                                        handleMobileMenuItemClick(
                                                          [data.text, menuItem.text, subMenuItem.text, nestedSubMenuItem.text],
                                                          nestedSubMenuItem.children,
                                                        )}
                                                      href={nestedSubMenuItem.href}
                                                    >
                                                      {nestedSubMenuItem.text}
                                                    </HeaderLink>
                                                  </NavigationMenu.Item>
                                                ))}
                                              </Navbar.List>
                                            </NavigationMenu.Sub>
                                          </Navbar.Content>
                                        </>
                                      )
                                    : (
                                        <HeaderLink
                                          onClick={() =>
                                            handleMobileMenuItemClick(
                                              [data.text, menuItem.text, subMenuItem.text],
                                              subMenuItem.children,
                                            )}
                                          href={subMenuItem.href}
                                        >
                                          {subMenuItem.text}
                                        </HeaderLink>
                                      )}
                                </NavigationMenu.Item>
                              ))}
                            </Navbar.List>
                            <NavigationMenu.Viewport />
                          </NavigationMenu.Sub>
                        </Navbar.Content>
                      </>
                    )
                  : (
                      <HeaderLink
                        onClick={() => handleMobileMenuItemClick([data.text, menuItem.text], menuItem.children)}
                        href={menuItem.href}
                      >
                        {menuItem.text}
                      </HeaderLink>
                    )}
              </Navbar.Item>
            ))}

            <li className={styles.line} role="separator" aria-hidden="true" />

            {extraData?.map((extraItem, extraItemKey) => (
              <Navbar.Item key={extraItemKey}>
                <HeaderLink
                  onClick={() => handleMobileMenuItemClick([extraItem.text], extraItem.children)}
                  href={extraItem.href}
                >
                  <Typography>{extraItem.text}</Typography>
                </HeaderLink>
              </Navbar.Item>
            ))}
          </Navbar.List>
        </Navbar>
      </Sheet.Content>
    </Sheet.Root>
  )
}
